import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { UrlService } from './url.service';
import { LoanActionType } from '../enums/loan-action-type.enum';
import { ContractRequest, ICallbackData, IContract, IInternalOffers, IPromoCode } from '../models/models';
import { ClientModel } from '../models/client.model';
import { Loan } from '../models/loan.model';
import { LoanHistoryInterface } from '../interfaces/loan-history.interface';
import { IScheduleEvent } from '../interfaces/schedule-event';
import { PromoDetails } from '@libs/models/promoDetails.model';
import { DocumentType } from '@libs/enums/document-param.emun';
import { IApplyRequest } from '@libs/models/apply-request.interface';
import { PaymanyTypeEnum } from '@libs/enums/paymany-type.enum';

@Injectable()
export class LoanService {
  constructor(
    private http: HttpClient,
    private urlService: UrlService,
  ) {}

  getInternalOffer(offerId: number, loanId: number): Observable<IInternalOffers> {
    return this.http.get<IInternalOffers>(`${this.urlService.host}/loans/${loanId}/internal-offers/${offerId}`);
  }

  post(application: IApplyRequest): Observable<Loan> {
    return this.http.post<Loan>(this.urlService.applicationUrl, application);
  }

  testPost(application: Loan): Observable<Loan> {
    return this.http.post<Loan>(this.urlService.testApplicationUrl, application);
  }

  postBankIdCallback(callbackData: ICallbackData, skipInterceptor: boolean = false): Observable<ClientModel> {
    return this.http.post<ClientModel>(this.urlService.bankIdCallback, callbackData, {
      headers: new HttpHeaders().set('skipErrorInterceptor', skipInterceptor.toString()),
    });
  }

  get(id: number): Observable<Loan> {
    return this.http.get<Loan>(`${this.urlService.loanUrl}/${id}`);
  }

  getLast(): Observable<Loan> {
    return this.http.get<Loan>(`${this.urlService.applicationUrl}${'?last'}`);
  }

  getCurrentLoan(): Observable<Loan[]> {
    return this.http.get<Loan[]>(this.urlService.loanUrl);
  }

  updateAll(): Observable<any> {
    return this.http.post(this.urlService.loanUrl, {});
  }

  paymentLoan(id: number, amount: number, url: string, reason: PaymanyTypeEnum): Observable<any> {
    const body = {
      amount,
      url,
      reason,
    };
    return this.http.post(`${this.urlService.loanContactUrl}/${id}/payments`, body);
  }

  getWithDocuments(): Observable<any> {
    return this.http.get<Loan>(`${this.urlService.loanUrl}?documents`);
  }

  getHistory(): Observable<LoanHistoryInterface[]> {
    return this.http.get<LoanHistoryInterface[]>(`${this.urlService.loanUrl}`);
  }

  getContractByType(id: number, type: DocumentType): Observable<IContract> {
    return this.http.get(`${this.urlService.loanContactUrl}/${id}/documents?${type}`);
  }

  getOperationContractByType(id: number, bindingId: number, type: DocumentType): Observable<IContract> {
    return this.http.get(`${this.urlService.loanContactUrl}/${id}/documents?${type}&operationBindingId=${bindingId}`);
  }

  putLoanAmount(id: number, amount: string | number): Observable<IContract> {
    if (!amount) return of(null);
    return this.http.put(`${this.urlService.loanContactUrl}/${id}?amount=${amount}`, {});
  }

  signContract(data: ContractRequest, skipInterceptor: boolean = false): Observable<Loan> {
    const body = {
      verificationToken: data.token,
      type: data.type,
      operationBindingId: data.operationBindingId,
      operationId: data.operationId,
    };
    return this.http.post<Loan>(`${this.urlService.loanContactUrl}/${data.loanId}/actions`, body, {
      headers: new HttpHeaders().set('skipErrorInterceptor', skipInterceptor.toString()),
    });
  }

  loanAction(id: number, type: LoanActionType): Observable<Loan> {
    const body = { type };
    return this.http.post<Loan>(`${this.urlService.loanContactUrl}/${id}/actions`, body);
  }

  getDiscountAgreement(id: number): Observable<IContract> {
    return this.http.post(`${this.urlService.loanContactUrl}/${id}/discounts`, {});
  }

  usePromoCode(code: string): Observable<IPromoCode> {
    return this.http.get<IPromoCode>(`${this.urlService.promoCampaign}/?promoCode=${code}`);
  }

  getSchedule(id: number): Observable<IScheduleEvent[]> {
    return this.http.get<IScheduleEvent[]>(`${this.urlService.scheduleUrl}/${id}/expected-payments`);
  }

  getPromoDetails(loanId: number, code: string): Observable<PromoDetails> {
    return this.http.get<PromoDetails>(this.urlService.promoUrl.replace('{loan_id}', `${loanId}`).replace('{code}', code));
  }

  applyPromo(loanId: number, code: string): Observable<any> {
    return this.http.post(this.urlService.promoUrl.replace('{loan_id}', `${loanId}`).replace('{code}', code), {});
  }

  decision(id: number, amount: number, term: number): Observable<Loan> {
    const body = {
      amount,
      term,
    };
    return this.http.put<Loan>(`${this.urlService.loanContactUrl}/${id}?amount=${amount}&term=${term}`, body);
  }

  pairedCode(token: string): Observable<{ code: string }> {
    const body = {
      token,
    };
    let params = new HttpParams();
    params = params.append('withCredentials', 'true');
    return this.http.post<{
      code: string;
    }>(`${this.urlService.verificationUrl}/paired-codes`, body, { params });
  }
}
